/**
 * Lightweight liveness helpers used by the proactive wake probe.
 *
 * On free hosting tiers (e.g. Render) the API container sleeps after inactivity
 * and takes ~30–60s to wake. A cheap, anonymous `/health` ping lets the app wake
 * the container and show a "waking up" overlay *before* the user's first real
 * request — separate from, and complementary to, the reactive reachability that
 * `client.ts` derives from actual API traffic.
 *
 * These use raw `fetch` deliberately, bypassing the axios interceptors/retry so
 * a probe never itself perturbs the reactive store or auth flow.
 */

/**
 * Derive the health-probe URL for a given API base URL: strip a trailing `/api`
 * segment (and slashes) and append `/health`.
 */
export function healthUrlFor(baseURL: string): string {
  const origin = baseURL.replace(/\/+$/, '').replace(/\/api$/, '');
  return `${origin}/health`;
}

/**
 * Fire a single liveness probe. Resolves to `true` only on a 2xx response; any
 * non-ok status, timeout, or network error resolves to `false` (the expected
 * shape while the server is asleep or unreachable).
 */
export async function pingHealth(url: string, timeoutMs = 8000): Promise<boolean> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, { method: 'GET', signal: controller.signal, cache: 'no-store' });
    return response.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

/* ─── Probe cache ──────────────────────────────────────────────
   A successful probe is trusted for 30 minutes so `/health` is hit at most once
   per window per client, not on every load/navigation. */

/** localStorage key holding the epoch-ms of the last successful health probe. */
export const HEALTH_CACHE_KEY = 'doccareplus.server_health_checked_at';

/** How long a successful probe is trusted before we probe again. */
export const HEALTH_CACHE_TTL_MS = 30 * 60 * 1000;

/** True when a recent successful probe means we can skip probing again. */
export function isProbeCacheFresh(
  now: number,
  lastOkAt: number | null,
  ttlMs: number = HEALTH_CACHE_TTL_MS
): boolean {
  if (lastOkAt == null) return false;
  return now - lastOkAt < ttlMs;
}

/** Read the cached last-healthy timestamp, or null if absent/corrupt. */
export function readLastHealthyAt(storage: Storage = window.localStorage): number | null {
  const raw = storage.getItem(HEALTH_CACHE_KEY);
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Persist the last-healthy timestamp so subsequent loads can skip the probe. */
export function writeLastHealthyAt(now: number, storage: Storage = window.localStorage): void {
  storage.setItem(HEALTH_CACHE_KEY, String(now));
}
